import React, { useContext, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import LogoSquare from '../../assets/plagaware-logo-square-50x50.png';
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { AppContext, DispatchContext } from "../../AppContext";
import * as actions from "../../actions/actions";
import Service from "../../services/service";
import { Paper, Grid, Typography, Button, TextField } from "@material-ui/core";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import FullScreenPage from "../../components/FullScreenPage";
import LoadingOverlayButton from "../../components/LoadingOverlayButton";
import { showCaptcha, getCookie, setCookie, config, resolveNotificationMessage, navigateToBackendUrl } from '../../helpers/constants';
import PasteDialog from "../../components/PasteDialog";
import ReactMarkdown from "react-markdown";


const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 3),
    height: 48
  },
}));

export default function Login(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const classes = useStyles();
  const [userDetail, setUserDetail] = React.useState({
    userName: '',
    password: ''
  });
  const [ssoName, setSsoName] = React.useState("");
  const [ssoCustomText, setSsoCustomText] = React.useState("");
  const [ssoHideMailLogin, setSsoHideMailLogin] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const isLoadingRef = React.useRef(false);
  const setLoading = (v) => { isLoadingRef.current = v; setIsLoading(v); };
  const s = state.strings;
  const { sso } = useParams();
  const location = useLocation();
  const revokeContractAction = new URLSearchParams(location.search).get('action') === 'revokeContract';


  useEffect(() => {
    if (!state.user.loggedIn) {
      showCaptcha();
    }
    if (state.user.sessionId) {
      Service.getNextMessage().then((res) => {
        if (res.success && res.message) {
          dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.message, s.notificationMessage) }));
        }
      });
    }
  }, []);

  useEffect(() => {
    const cookieSSO = getCookie(config.ssoDomainCookieName);
    if ((!sso) && cookieSSO) {
      dispatch(actions.navigate_to("/login/" + cookieSSO));
    }
  }, []);

  useEffect(() => {
    if (sso) {
      Service.getSSOInfo(sso).then((res) => {
        if (res.success) {
          setSsoName(res.data.friendlyName);
          setSsoCustomText(res.data.customText);
          setSsoHideMailLogin(res.data.hideMailLogin);
        } else {
          dispatch(actions.navigate_to("/login/"));
        }
      });
    }
  }, [sso]);



  const handleLogin = () => {
    if (isLoadingRef.current) return;
    setLoading(true);
    captchaAction("login", doLogin, () => setLoading(false));
  }

  const handleSSOLogin = () => {
    captchaAction("ssologin", doSSOLogin);
  }

  const captchaAction = (action, fn, onFail) => {
    const captchas = document.getElementsByClassName("grecaptcha-badge");
    if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
          Service.verifyReCaptcha(token).then((verifyRes) => {
            if (verifyRes.success) {
              fn();
            } else {
              if (onFail) onFail();
              dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
            }
          });
        });
      });
    } else {
      fn();
    }
  }

  const doLogin = () => {
    Service.login(userDetail.userName, userDetail.password).then(res => {
      if (res.success) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          dispatch(actions.navigate_to(revokeContractAction ? '/?action=revokeContract' : '/dashboard'));
          dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedIn.format(getInfoRes.data.mail), status: true }));
        });
      } else {
    setLoading(false);
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }


  const doSSOLogin = () => {
    navigateToBackendUrl("login?sso=" + sso);
  }

  const handleGuestLogin = () => {
    if (isLoadingRef.current) return;
    setLoading(true);
    captchaAction("guestLogin", doGuestLogin, () => setLoading(false));
  }

  const doGuestLogin = () => {
    dispatch(actions.showFullscreenLoading(true, s.loginDialog.creatingGuestAccount));
    Service.createGuestUser().then(res => {
      if (res.success) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          dispatch(actions.navigate_to("/dashboard"));
          dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedIn.format(getInfoRes.data.mail), status: true }));
          dispatch(actions.showFullscreenLoading(false));
        });
      } else {
        setLoading(false);
        dispatch(actions.openToast({ type: "error", message: res.errors[0], status: true }));
        dispatch(actions.showFullscreenLoading(false));
      }
    });
  }


  const showSSOHelp = () => {
    dispatch(actions.openPasteDialog({
      title: s.loginDialog.ssoLoginWithOrganization,
      hint: s.loginDialog.ssoLoginHint,
      description: s.loginDialog.ssoDescription,
      placeholder: s.loginDialog.emailPlaceholder,
      prefill: userDetail.userName,
      okLabel: s.ok,
      cancelLabel: s.cancel,
      onConfirm: onForwardToSSO
    }));
  }

  const onForwardToSSO = (payload, mail) => {
    Service.getSSOInfoFromMail(mail).then(res => {
      if (res.success) {
        navigateToBackendUrl(s.links.login + "?sso=" + res.data.name);
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }



  const handleChange = (value, key) => {
    setUserDetail({
      ...userDetail,
      [key]: value
    })
  }

  return (
    <FullScreenPage>
      <PasteDialog />
      <Container component="main" style={{ width: 450 }}>
        <Paper className={classes.paper}>
          <img src={LogoSquare} alt="logo" style={{ margin: 16 }} />
          <Typography component="h1" variant="h5" paragraph>
            {s.loginDialog.loginHeading}
          </Typography>

          {revokeContractAction &&
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: "#e8f0fe", border: "1px solid #c5d3f0", borderRadius: 4, padding: "12px 16px", marginBottom: 16, width: "100%" }}>
              <InfoOutlinedIcon color="primary" style={{ marginRight: 8, flexShrink: 0, marginTop: 2 }} />
              <Typography variant="body2">{s.loginDialog.revokeContractLoginHint}</Typography>
            </div>
          }

          {!sso && <>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={s.loginDialog.mail}
              name="email"
              autoComplete="email"
              autoFocus
              value={userDetail.userName}
              onChange={(evt) => handleChange(evt.target.value, 'userName')} />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={s.loginDialog.pwd}
              type="password"
              id="password"
              autoComplete="current-password"
              value={userDetail.password}
              onChange={(evt) => handleChange(evt.target.value, 'password')} />

            <LoadingOverlayButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              loading={isLoading}
              disabled={!userDetail.userName || !userDetail.password}
              className={classes.submit}
              onClick={handleLogin}>
              {s.loginDialog.login}
            </LoadingOverlayButton>
            <Grid container>
              <Grid item xs>
                <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/reset'))}>
                  {s.loginDialog.forgotPwd}
                </Link>
              </Grid>
              <Grid item>
                <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/signup'))}>
                  {s.loginDialog.signUpLink}
                </Link>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs>
                <Link component="button" type="button" variant="body2" onClick={() => showSSOHelp()}>
                  {s.loginDialog.ssoLoginWithOrganization}
                </Link>
              </Grid>
              <Grid item>
                {state.user.loggedIn ?
                  <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/dashboard'))}>
                    {s.loginDialog.continueAs.format(state.user.mail)}
                  </Link> :
                  <Link component="button" type="button" variant="body2" onClick={() => handleGuestLogin()}>
                    {s.loginDialog.guest}
                  </Link>
                }
              </Grid>
            </Grid>
          </>
          }
          {sso &&
            <>
              <Typography>{s.loginDialog.ssoLoginTitle}</Typography>
              <Typography align="center" style={{ marginTop: 8 }}>{ssoName}</Typography>
              <Typography style={{ marginTop: 16 }} variant="body2" color="secondary">{s.loginDialog.ssoLoginFavoriteHint}</Typography>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={() => handleSSOLogin()}>
                {s.loginDialog.ssoLoginWithOrganization}
              </Button>
              {!ssoHideMailLogin &&
                <>
                  <Grid container>
                    <Grid item xs>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs>
                      <Link component="button" type="button" variant="body2" onClick={() => { setCookie(config.ssoDomainCookieName, "", 0); dispatch(actions.navigate_to('/login')); }}>
                        {s.loginDialog.loginWithMailAndPwd}
                      </Link>
                    </Grid>

                    <Grid item>
                      {state.user.loggedIn ?
                        <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/dashboard'))}>
                          {s.loginDialog.continueAs.format(state.user.mail)}
                        </Link> :
                        <Link component="button" type="button" variant="body2" onClick={() => handleGuestLogin()}>
                          {s.loginDialog.guest}
                        </Link>
                      }
                    </Grid>
                  </Grid>
                </>
              }
              {ssoCustomText &&
                <ReactMarkdown>{ssoCustomText}</ReactMarkdown>}
            </>
          }
        </Paper>
      </Container>
    </FullScreenPage>
  );
}
